import type { $Fetch } from 'ofetch';
import type {
  AgendamentoGateway, CalendarioParams,
} from '@/application/port/out/AgendamentoGateway';
import type {
  Agendamento, CancelarAgendamentoCommand, CriarAgendamentoCommand,
} from '@/domain/model/Agendamento';
import type { Page } from '@/domain/model/Page';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

/**
 * HTTP gateway pro calendário + ciclo do agendamento admin.
 * Authorities: acadadmin:agendamento:{read,create,manage,cancelar}.
 *
 * @since G.5.10
 */
export class HttpAgendamentoGateway implements AgendamentoGateway {
  constructor(private readonly client: $Fetch) {}

  async calendario(params: CalendarioParams): Promise<Page<Agendamento>> {
    const query: Record<string, string | number> = {
      inicio: params.inicio,
      fim: params.fim,
      page: params.page ?? 0,
      size: params.size ?? 50,
    };
    if (params.poloId) query.poloId = params.poloId;
    if (params.tipo) query.tipo = params.tipo;
    if (params.status) query.status = params.status;
    const res = await this.client<ApiResponse<Page<Agendamento>>>(
      '/api/v1/admin/agendamentos', { method: 'GET', params: query });
    assertApiSuccess(res);
    return res.data;
  }

  async get(id: string): Promise<Agendamento> {
    const res = await this.client<ApiResponse<Agendamento>>(
      `/api/v1/admin/agendamentos/${encodeURIComponent(id)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async criar(cmd: CriarAgendamentoCommand): Promise<Agendamento> {
    const res = await this.client<ApiResponse<Agendamento>>(
      '/api/v1/admin/agendamentos', { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async confirmar(id: string): Promise<Agendamento> {
    const res = await this.client<ApiResponse<Agendamento>>(
      `/api/v1/admin/agendamentos/${encodeURIComponent(id)}/confirmar`,
      { method: 'POST' });
    assertApiSuccess(res);
    return res.data;
  }

  async marcarRealizado(id: string): Promise<Agendamento> {
    const res = await this.client<ApiResponse<Agendamento>>(
      `/api/v1/admin/agendamentos/${encodeURIComponent(id)}/realizado`,
      { method: 'POST' });
    assertApiSuccess(res);
    return res.data;
  }

  async marcarNoShow(id: string): Promise<Agendamento> {
    const res = await this.client<ApiResponse<Agendamento>>(
      `/api/v1/admin/agendamentos/${encodeURIComponent(id)}/no-show`,
      { method: 'POST' });
    assertApiSuccess(res);
    return res.data;
  }

  async cancelar(id: string, cmd: CancelarAgendamentoCommand): Promise<Agendamento> {
    const res = await this.client<ApiResponse<Agendamento>>(
      `/api/v1/admin/agendamentos/${encodeURIComponent(id)}/cancelar`,
      { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
}
